import { create } from 'zustand'
import type {
  Clip,
  Grade,
  Lang,
  LogoConfig,
  LogoPos,
  Range,
  Reframe,
  ReframeMode,
  StyleDna,
  StyleStats,
  Word,
} from './types'
import type { FormatKey } from './lib/formats'
import { computeKeptRanges } from './lib/ranges'

let cidCounter = 0
const cid = () => `clip_${Date.now().toString(36)}_${cidCounter++}`

const DEFAULT_LOGO: LogoConfig = {
  url: null,
  file: null,
  pos: 'br', // góc dưới-phải: che watermark Gemini/Veo trên Google Flow
  scale: 0.15,
  opacity: 1, // đục 100% để che hoàn toàn logo bên dưới
  margin: 0.03,
}

const NEUTRAL_GRADE: Grade = { brightness: 1, contrast: 1, saturate: 1, warmth: 0 }
const DEFAULT_STYLE: StyleDna = { refName: null, ref: null, grade: NEUTRAL_GRADE, apply: false }

/** Một phiên bản đã lưu của quyết định biên tập (GĐ8 — version history). */
export interface Snapshot {
  id: string
  label: string
  createdAt: number
  clips: { id: string; name: string; words: Word[]; cuts: Range[] }[]
  style: StyleDna
  exportFormats: FormatKey[]
}

/** Cấu trúc file dự án .json (không chứa video, chỉ quyết định biên tập). */
export interface ProjectFile {
  app: 'brandcut'
  version: 1
  clips: { name: string; words: Word[]; cuts: Range[] }[]
  style: StyleDna
  exportFormats: FormatKey[]
}

interface State {
  clips: Clip[]
  activeClipId: string | null
  logo: LogoConfig
  exportFormats: FormatKey[]
  reframe: Reframe
  style: StyleDna
  studioAudio: boolean
  history: Snapshot[]
  onboardingSeen: boolean
  showAbout: boolean

  // playback / UI
  playing: boolean
  currentTime: number // thời gian nguồn trong clip đang active
  pendingSeek: number | null
  status: string
  busy: boolean
  lang: Lang

  // clip actions
  addClip: (file: File, meta: { duration: number; width: number; height: number }) => void
  removeClip: (id: string) => void
  moveClip: (id: string, dir: -1 | 1) => void
  setActive: (id: string) => void
  seekTo: (clipId: string, time: number) => void
  clearPendingSeek: () => void

  // editing actions (áp lên clip đang active)
  setClipWords: (clipId: string, words: Word[]) => void
  toggleWord: (wordId: string) => void
  removeAllFillers: () => void
  restoreAll: () => void
  addCuts: (clipId: string, ranges: Range[]) => void
  clearCuts: (clipId: string) => void
  trimActiveTo: (seconds: number) => void

  // Style DNA (GĐ5)
  setStyle: (refName: string, ref: StyleStats, grade: Grade) => void
  setGrade: (grade: Partial<Grade>) => void
  toggleStyleApply: () => void
  clearStyle: () => void

  // Studio audio (GĐ6)
  toggleStudioAudio: () => void

  // Version history / project (GĐ8)
  saveVersion: (label: string) => void
  restoreVersion: (id: string) => void
  deleteVersion: (id: string) => void
  applyProject: (p: ProjectFile) => number
  dismissOnboarding: () => void
  setShowAbout: (b: boolean) => void

  // logo actions
  setLogoFile: (file: File) => void
  clearLogo: () => void
  setLogoPos: (pos: LogoPos) => void
  setLogoScale: (scale: number) => void
  setLogoOpacity: (opacity: number) => void

  // export formats / reframe
  toggleFormat: (key: FormatKey) => void
  setReframeMode: (mode: ReframeMode) => void
  setFocus: (fx: number, fy: number) => void

  // misc
  setPlaying: (p: boolean) => void
  setCurrentTime: (t: number) => void
  setStatus: (s: string) => void
  setBusy: (b: boolean) => void
  setLang: (l: Lang) => void
  reset: () => void
}

export const useStore = create<State>((set, get) => ({
  clips: [],
  activeClipId: null,
  logo: DEFAULT_LOGO,
  exportFormats: ['original', 'r9x16'],
  reframe: { mode: 'center', fx: 0.5, fy: 0.5 },
  style: DEFAULT_STYLE,
  studioAudio: false,
  history: [],
  onboardingSeen: false,
  showAbout: false,
  playing: false,
  currentTime: 0,
  pendingSeek: null,
  status: '',
  busy: false,
  lang: 'auto',

  addClip: (file, meta) =>
    set((s) => {
      const clip: Clip = {
        id: cid(),
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        duration: meta.duration,
        width: meta.width,
        height: meta.height,
        words: [],
        transcribed: false,
        cuts: [],
      }
      return {
        clips: [...s.clips, clip],
        activeClipId: s.activeClipId ?? clip.id,
        status: `Đã thêm clip: ${file.name}`,
      }
    }),

  removeClip: (id) =>
    set((s) => {
      const clip = s.clips.find((c) => c.id === id)
      if (clip) URL.revokeObjectURL(clip.url)
      const clips = s.clips.filter((c) => c.id !== id)
      const activeClipId =
        s.activeClipId === id ? (clips[0]?.id ?? null) : s.activeClipId
      return { clips, activeClipId, playing: false }
    }),

  moveClip: (id, dir) =>
    set((s) => {
      const idx = s.clips.findIndex((c) => c.id === id)
      const j = idx + dir
      if (idx < 0 || j < 0 || j >= s.clips.length) return {}
      const clips = [...s.clips]
      ;[clips[idx], clips[j]] = [clips[j], clips[idx]]
      return { clips }
    }),

  setActive: (id) => set({ activeClipId: id, currentTime: 0 }),

  seekTo: (clipId, time) =>
    set({ activeClipId: clipId, pendingSeek: time, currentTime: time }),

  clearPendingSeek: () => set({ pendingSeek: null }),

  setClipWords: (clipId, words) =>
    set((s) => ({
      clips: s.clips.map((c) =>
        c.id === clipId ? { ...c, words, transcribed: true } : c,
      ),
    })),

  toggleWord: (wordId) =>
    set((s) => ({
      clips: s.clips.map((c) =>
        c.id === s.activeClipId
          ? {
              ...c,
              words: c.words.map((w) =>
                w.id === wordId ? { ...w, removed: !w.removed } : w,
              ),
            }
          : c,
      ),
    })),

  removeAllFillers: () =>
    set((s) => ({
      clips: s.clips.map((c) =>
        c.id === s.activeClipId
          ? { ...c, words: c.words.map((w) => (w.isFiller ? { ...w, removed: true } : w)) }
          : c,
      ),
    })),

  restoreAll: () =>
    set((s) => ({
      clips: s.clips.map((c) =>
        c.id === s.activeClipId
          ? { ...c, words: c.words.map((w) => ({ ...w, removed: false })), cuts: [] }
          : c,
      ),
    })),

  addCuts: (clipId, ranges) =>
    set((s) => ({
      clips: s.clips.map((c) =>
        c.id === clipId ? { ...c, cuts: [...c.cuts, ...ranges] } : c,
      ),
    })),

  clearCuts: (clipId) =>
    set((s) => ({
      clips: s.clips.map((c) => (c.id === clipId ? { ...c, cuts: [] } : c)),
    })),

  // Giữ lại `seconds` giây đầu của bản dựng (clip active), cắt phần còn lại.
  trimActiveTo: (seconds) =>
    set((s) => {
      const c = s.clips.find((x) => x.id === s.activeClipId)
      if (!c) return {}
      // duyệt các khoảng giữ, tích lũy tới `seconds`, cắt phần vượt.
      const removedRanges = [
        ...c.words.filter((w) => w.removed).map((w) => ({ start: w.start, end: w.end })),
        ...c.cuts,
      ]
      // kept ranges tạm để tìm mốc cắt
      const kept = computeKeptRanges(c.duration, removedRanges)
      let acc = 0
      let cutFrom = c.duration
      for (const r of kept) {
        const len = r.end - r.start
        if (acc + len >= seconds) {
          cutFrom = r.start + (seconds - acc)
          break
        }
        acc += len
      }
      if (cutFrom >= c.duration) return {}
      return {
        clips: s.clips.map((x) =>
          x.id === c.id ? { ...x, cuts: [...x.cuts, { start: cutFrom, end: c.duration }] } : x,
        ),
      }
    }),

  setStyle: (refName, ref, grade) =>
    set((s) => ({ style: { ...s.style, refName, ref, grade, apply: true } })),

  setGrade: (grade) => set((s) => ({ style: { ...s.style, grade: { ...s.style.grade, ...grade } } })),

  toggleStyleApply: () => set((s) => ({ style: { ...s.style, apply: !s.style.apply } })),

  clearStyle: () => set({ style: DEFAULT_STYLE }),

  toggleStudioAudio: () => set((s) => ({ studioAudio: !s.studioAudio })),

  saveVersion: (label) =>
    set((s) => {
      const snap: Snapshot = {
        id: `v_${Date.now().toString(36)}`,
        label: label || `Phiên bản ${s.history.length + 1}`,
        createdAt: Date.now(),
        clips: s.clips.map((c) => ({
          id: c.id,
          name: c.name,
          words: c.words.map((w) => ({ ...w })),
          cuts: c.cuts.map((r) => ({ ...r })),
        })),
        style: JSON.parse(JSON.stringify(s.style)),
        exportFormats: [...s.exportFormats],
      }
      return { history: [snap, ...s.history].slice(0, 20), status: `💾 Đã lưu "${snap.label}".` }
    }),

  restoreVersion: (id) =>
    set((s) => {
      const snap = s.history.find((h) => h.id === id)
      if (!snap) return {}
      const clips = s.clips.map((c) => {
        const sc = snap.clips.find((x) => x.id === c.id) ?? snap.clips.find((x) => x.name === c.name)
        return sc ? { ...c, words: sc.words.map((w) => ({ ...w })), cuts: sc.cuts.map((r) => ({ ...r })) } : c
      })
      return {
        clips,
        style: JSON.parse(JSON.stringify(snap.style)),
        exportFormats: [...snap.exportFormats],
        status: `↩️ Đã khôi phục "${snap.label}".`,
      }
    }),

  deleteVersion: (id) => set((s) => ({ history: s.history.filter((h) => h.id !== id) })),

  // Áp file dự án đã mở lên các clip đang có (khớp theo tên). Trả về số clip khớp.
  applyProject: (p) => {
    let matched = 0
    set((s) => {
      const clips = s.clips.map((c) => {
        const pc = p.clips.find((x) => x.name === c.name)
        if (!pc) return c
        matched++
        return { ...c, words: pc.words.map((w) => ({ ...w })), cuts: pc.cuts.map((r) => ({ ...r })), transcribed: pc.words.length > 0 }
      })
      return {
        clips,
        style: JSON.parse(JSON.stringify(p.style)),
        exportFormats: [...p.exportFormats],
      }
    })
    return matched
  },

  dismissOnboarding: () => set({ onboardingSeen: true }),
  setShowAbout: (showAbout) => set({ showAbout }),

  setLogoFile: (file) =>
    set((s) => {
      if (s.logo.url) URL.revokeObjectURL(s.logo.url)
      return { logo: { ...s.logo, file, url: URL.createObjectURL(file) } }
    }),

  clearLogo: () =>
    set((s) => {
      if (s.logo.url) URL.revokeObjectURL(s.logo.url)
      return { logo: { ...s.logo, file: null, url: null } }
    }),

  setLogoPos: (pos) => set((s) => ({ logo: { ...s.logo, pos } })),
  setLogoScale: (scale) => set((s) => ({ logo: { ...s.logo, scale } })),
  setLogoOpacity: (opacity) => set((s) => ({ logo: { ...s.logo, opacity } })),

  toggleFormat: (key) =>
    set((s) => {
      const has = s.exportFormats.includes(key)
      const next = has
        ? s.exportFormats.filter((k) => k !== key)
        : [...s.exportFormats, key]
      return { exportFormats: next.length ? next : s.exportFormats } // luôn giữ ít nhất 1
    }),

  setReframeMode: (mode) => set((s) => ({ reframe: { ...s.reframe, mode } })),
  setFocus: (fx, fy) => set({ reframe: { mode: 'manual', fx, fy } }),

  setPlaying: (playing) => set({ playing }),
  setCurrentTime: (currentTime) => set({ currentTime }),
  setStatus: (status) => set({ status }),
  setBusy: (busy) => set({ busy }),
  setLang: (lang) => set({ lang }),

  reset: () => {
    const s = get()
    s.clips.forEach((c) => URL.revokeObjectURL(c.url))
    if (s.logo.url) URL.revokeObjectURL(s.logo.url)
    set({
      clips: [],
      activeClipId: null,
      logo: DEFAULT_LOGO,
      exportFormats: ['original', 'r9x16'],
      reframe: { mode: 'center', fx: 0.5, fy: 0.5 },
      style: DEFAULT_STYLE,
      studioAudio: false,
      history: [],
      showAbout: false,
      playing: false,
      currentTime: 0,
      pendingSeek: null,
      status: '',
    })
  },
}))

export const selectActiveClip = (s: State): Clip | undefined =>
  s.clips.find((c) => c.id === s.activeClipId)
